import * as logMessageTypes from '../../consts/app/logMessageTypes';
import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as gamMethods from '../../consts/gam/gamMethods';
import * as uamMethods from '../../consts/uam/uamMethods';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import {setFWUError, setFWUStarted, setFWUSuccess, updateIotDeviceData} from '../../state/ducks/iotDevice/actions';
import {setMwDeviceFirmwareData} from '../../state/ducks/mwDevice';
import {
    setDeviceInfoReceived,
    setHolderInfoReceived,
    setYapDeviceConnected,
    setYapFirmwareInProgress,
    setYapHolderConnected,
    setYapLastRequestId,
} from '../../state/ducks/yapEncrypted';
import {makeSelectIotDeviceData} from '../../state/selectors/iotDevice';
import {selectMwDeviceInternalId} from '../../state/selectors/mwDevice';
import {
    makeSelectAssetIdByType,
    makeSelectIsHolderByAssetId,
    makeSelectIsOfflineYapFirmwareInProgress,
    makeSelectIsYapFirmwareInProgress,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import backendService from '../app/backendService';
import cmService from '../communicationLayer/cmService';
import fwuTargetService from '../device/fwuTargetService';
import fwuPackageService from '../fwu/fwuPackageService';
import GamClient from '../gam/gamClient';
import log from '../logger/log';
import UamClient from '../uam/uamClient';
import yapClientService from '../yap/yapClientService';
import yapConnectionService from '../yap/yapConnectionService';
import yapFlowTimeoutService from '../yap/yapFlowTimeoutService';

const getFirmwareAvailability = async (fwuTarget) => {
    const isHolder = fwuTargetService.isFwuTargetHolderRelated(fwuTarget);
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (!assetId) return false;

    const isGamBackend = backendService.isGamBackend();
    const firmwareResponse = isGamBackend
        ? await new GamClient().callGet({
              methodName: gamMethods.GET_FIRMWARE_AVAILABILITY,
              requestConfig: {args: assetId},
              isNeedRedirectForOtherErrors: false,
          })
        : await new UamClient().callGet({
              methodName: uamMethods.GET_FIRMWARE_AVAILABILITY,
              requestConfig: {args: assetId},
              isNeedRedirectForOtherErrors: false,
          });

    if (!firmwareResponse) return false;

    updateFirmwareAvailableVersion(isHolder, firmwareResponse);
    return true;
};

const getFirmwareVersions = async (fwuTarget, onFirmwareCheck) => {
    const isHolder = fwuTargetService.isFwuTargetHolderRelated(fwuTarget);
    const {firmwareVersion, engineFirmwareVersion, bleStackFirmwareVersion} =
        fwuPackageService.getAvailableFirmwareVersions(isHolder);

    if (firmwareVersion || engineFirmwareVersion || bleStackFirmwareVersion) {
        return {firmwareVersion, engineFirmwareVersion, bleStackFirmwareVersion};
    }

    //check fwu if no available_version found
    await onFirmwareCheck(fwuTarget);
    return fwuPackageService.getAvailableFirmwareVersions(isHolder);
};

const updateFirmware = async (fwuTarget, onFirmwareCheck) => {
    const isHolder = fwuTargetService.isFwuTargetHolderRelated(fwuTarget);
    const isYapFirmwareInProgress = makeSelectIsYapFirmwareInProgress()(getState());

    if (isYapFirmwareInProgress) return;

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.UPDATE_FIRMWARE, isHolder);

    dispatch(setYapFirmwareInProgress(true));
    dispatch(setFWUStarted(fwuTarget));

    const onError = () => {
        dispatch(setFWUError());
        dispatch(setYapFirmwareInProgress(false));
    };

    const isCancelSuccess = await yapClientService.waitUntilDataCollectionCanceled();

    if (!isCancelSuccess) return onError();

    const isGamBackend = backendService.isGamBackend();
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);
    const {firmwareVersion, engineFirmwareVersion, bleStackFirmwareVersion} = await getFirmwareVersions(
        fwuTarget,
        onFirmwareCheck
    );

    if (!assetId || (!firmwareVersion && !engineFirmwareVersion && !bleStackFirmwareVersion)) return onError();

    try {
        //forced reconnect to CM to check if sbl mode or not
        if (isHolder) {
            dispatch(setYapHolderConnected(false));
        } else {
            dispatch(setYapDeviceConnected(false));
        }

        await yapConnectionService.syncAndSubscribeOnDevice(isHolder);

        const isConnected = await cmService.waitUntilConnected({isHolder});

        if (!isConnected) return onError();

        log.info('Start firmware process.');
        const data = {};

        if (firmwareVersion) {
            data.firmwareVersion = firmwareVersion;
        }

        const associatedFirmwareVersions = {};

        if (bleStackFirmwareVersion) {
            associatedFirmwareVersions.BLE = {
                firmwareVersion: bleStackFirmwareVersion,
            };
        }

        if (engineFirmwareVersion) {
            associatedFirmwareVersions.engine = {
                firmwareVersion: engineFirmwareVersion,
            };
        }

        data.associatedFirmwareVersions = associatedFirmwareVersions;

        const {requestId} =
            (isGamBackend
                ? await new GamClient().callPost({
                      methodName: gamMethods.UPDATE_FIRMWARE,
                      requestConfig: {
                          args: assetId,
                          data,
                      },
                      isNeedRedirectForOtherErrors: false,
                  })
                : await new UamClient().callPost({
                      methodName: uamMethods.UPDATE_FIRMWARE,
                      requestConfig: {
                          args: assetId,
                          data,
                      },
                      isNeedRedirectForOtherErrors: false,
                  })) || {};

        if (!requestId) return onError();

        dispatch(setYapLastRequestId(requestId, isHolder));
        yapFlowTimeoutService.createFlowTimeout({
            type: yapFlowTypes.UPDATE_FIRMWARE,
            timeoutInSec: isGamBackend ? gamTimeouts.GAM_FIRMWARE_TIMEOUT : uamTimeouts.UAM_FIRMWARE_TIMEOUT,
            isHolder,
        });
    } catch (e) {
        log.error(`Update Firmware failed. error: ${e}`, isGamBackend ? logMessageTypes.GAM : logMessageTypes.UAM);
        onError();
    }
};

const updateFirmwareProgress = () => {
    // Ignore progress from IOT, progress value is set directly on MW side(in BaseFwuClient)
};

const proceedFWU = async (fwYapStatus, assetId, error) => {
    const state = getState();
    const isOfflineMode = makeSelectIsOfflineYapFirmwareInProgress()(state);

    if (isOfflineMode) return;

    if (fwYapStatus === yapActionStatusTypes.SUCCESS) {
        dispatch(setFWUSuccess());
        const isHolder = makeSelectIsHolderByAssetId(assetId)(getState());

        if (isHolder) {
            dispatch(setHolderInfoReceived(false));

            const data = {holder: {is_device_in_sbl_mode: false}};

            dispatch(updateIotDeviceData(data));
        } else {
            dispatch(setDeviceInfoReceived(false));
        }
    } else {
        dispatch(setFWUError(error));
    }
    dispatch(setYapFirmwareInProgress(false));
};

const updateFirmwareAvailableVersion = (isHolder, firmwareResponse) => {
    const state = getState();
    const deviceInternalId = selectMwDeviceInternalId(state);

    if (!deviceInternalId) return;

    const iotDevice = makeSelectIotDeviceData()(state);
    const {availableFirmwares, availableAssociatedFirmwares} = firmwareResponse || {};
    const {engine, BLE: bleStack} = availableAssociatedFirmwares || {};
    const firmware = getFirmwareData(availableFirmwares);
    const {package_available} = firmware;
    const firmwareData = isHolder ? {holder: firmware} : {device: firmware};
    const mwFirmwareData = isHolder ? {holder: {package_available}} : {device: {package_available}};

    dispatch(updateIotDeviceData(firmwareData));
    dispatch(setMwDeviceFirmwareData(mwFirmwareData));

    if (bleStack && !isHolder) {
        const firmware = getFirmwareData(bleStack);
        const {package_available} = firmware;
        const firmwareData = {ble_stack: firmware};
        const mwFirmwareData = {ble_stack: {package_available}};

        dispatch(updateIotDeviceData(firmwareData));
        dispatch(setMwDeviceFirmwareData(mwFirmwareData));
    }

    if (engine || (iotDevice.holder_engine && isHolder) || (iotDevice.device_engine && !isHolder)) {
        const firmware = getFirmwareData(engine);
        const {package_available} = firmware;
        const firmwareData = isHolder ? {holder_engine: firmware} : {device_engine: firmware};
        const mwFirmwareData = isHolder ? {holder_engine: {package_available}} : {device_engine: {package_available}};

        dispatch(updateIotDeviceData(firmwareData));
        dispatch(setMwDeviceFirmwareData(mwFirmwareData));
    }
};

const getFirmwareData = (availableFirmwares) => {
    const package_available = availableFirmwares && Array.isArray(availableFirmwares) && availableFirmwares.length;
    const fwVersion = package_available ? availableFirmwares[0]?.version : undefined;

    return {
        firmware: {
            package_available: !!package_available,
            availableFirmwares,
            available_version: fwVersion,
            isCheckInProgress: false,
        },
    };
};

export default {
    getFirmwareAvailability,
    proceedFWU,
    updateFirmware,
    updateFirmwareProgress,
};
